// Scrollable list of messages for one agent.
//
// Sprint 5.6 (D6) — centered column. The inner column is `max-w-3xl
// mx-auto` so prose lines are short enough to read comfortably; the
// surrounding scroll container retains its full-height surface.
//
// Sprint 5.6 (D9) — subagent grouping. Messages with parentToolUseId
// are pulled out of the main timeline and rendered inside the
// SubagentPanel for their parent tool_use.
//
// Sprint 5.6 (D25) — per-turn model badge. We compute the set of
// distinct models the agent has used at least once; if it's ≥2, every
// assistant_text gets a small model badge underneath.

import { useMemo } from 'react';
import { useTranslation } from '../i18n';
import type { Message } from '../types';
import { useSettingsStore } from '../stores/settings.store';
import {
  distinctModelsInHistory,
  groupBySubagent,
  pairToolResults,
  asToolUse,
  SUBAGENT_TOOL_NAMES,
} from '../lib/messages';
import { DeveloperOnly } from './founder/DeveloperOnly';
import { aggregateMessages, type RenderItem } from '../lib/tool-rendering/aggregate';
import { synthesizeTurnSummaries } from '../lib/tool-rendering/synthesize-summary';
import {
  collectTodoEmits as collectTodoEmitsLocal,
  deriveProgress,
  parseTodos,
} from '../lib/tool-rendering/derive-progress';
import { diffTodos } from '../lib/tool-rendering/diff-todos';
import { useChatScroll } from '../hooks/useChatScroll';
import { useInfiniteScrollUp } from '../hooks/useInfiniteScrollUp';
import { usePaginatedHistory } from '../hooks/usePaginatedHistory';
import { useAppStore } from '../stores/app.store';
import { MessageBubble } from './MessageBubble';
import { PartialIndicator } from './PartialIndicator';
import { StreamTruncatedPill } from './StreamTruncatedPill';
import { LiveActivity } from './LiveActivity';
import { ScrollToBottomPill } from './ScrollToBottomPill';
import { SubagentPanel } from './SubagentPanel';
import { AggregationCard } from './founder/AggregationCard';
import { TurnSummaryCard } from './founder/TurnSummaryCard';
import { StickyProgressBar } from './founder/StickyProgressBar';
// S6.5 — permission card cluster.
import { PermissionCard } from './permissions/PermissionCard';
import { AskUserQuestionCard } from './permissions/AskUserQuestionCard';
import {
  getRequestId,
  isAskUserQuestionPending,
  isPermissionPending,
  isPermissionTerminal,
  type AskUserQuestionPendingContent,
  type PermissionPendingContent,
  type PermissionTerminalContent,
} from '../lib/permissions/types';

export interface ConversationListProps {
  agentId: string;
  messages: Message[];
  // Sprint messages-section-fixes (D-msg-1) — Edit / Resend wiring.
  // ChatView supplies these handlers; ConversationList passes them
  // through to user-message bubbles (regular + sticky). When undefined
  // (e.g. read-only history view) the bubbles render without action
  // buttons, exactly as before this sprint.
  onEditMessage?: (text: string) => void;
  onResendMessage?: (text: string) => void;
  // Oficina Virtual F4 (spec 004 T7, amendment E-M5) — opt-in office skin.
  // Default undefined → DOM and classes byte-identical to the classic chat.
  // With 'office': the scroller gains `data-surface="office"`, the inner
  // column goes full-width (the drawer is already narrow), and the wrappers
  // of ASSISTANT-authored messages gain `data-office-accent`, which
  // index.css paints with the gold left border. System pills, permission
  // cards, user bubbles and SubagentPanel wrappers do NOT carry the
  // attribute (E-M5: never a descendant selector on data-message-id).
  surface?: 'office';
}

// Sprint 12 (D-008) — system message subtypes hidden in founder mode.
const HIDDEN_SYSTEM_SUBTYPES = new Set([
  'init', 'turn_boundary', 'result', 'exit',
  'stopped_by_user', 'respawning', 'respawned',
  'reconciled', 'crash', 'stuck', 'recovered',
]);

function getTodoInput(m: Message): Record<string, unknown> {
  const c = m.content;
  if (typeof c === 'object' && c !== null) {
    const input = (c as { input?: unknown }).input;
    if (typeof input === 'object' && input !== null) {
      return input as Record<string, unknown>;
    }
  }
  return {};
}

export function ConversationList({
  agentId,
  messages,
  onEditMessage,
  onResendMessage,
  surface,
}: ConversationListProps) {
  const { t } = useTranslation();
  // T7 (E-M5) — attribute bag spread onto assistant-message wrappers only.
  const office = surface === 'office';
  const officeAccent = office ? { 'data-office-accent': '' } : {};
  const partialText = useAppStore((s) => s.partialTextByAgent[agentId]);
  const partialActive = partialText !== undefined;
  // Sprint 17 — single in-flight indicator. PartialIndicator wins on
  // text streaming (the content forming IS the signal); LiveActivity
  // covers the gap during thinking + tool execution.
  const liveActivity = useAppStore((s) => s.liveActivityByAgent[agentId]);

  const { resultByToolUseId, pairedResultIds } = useMemo(
    () => pairToolResults(messages),
    [messages],
  );

  const { topLevel, childrenByParent, orphanNoticeIds } = useMemo(
    () => groupBySubagent(messages),
    [messages],
  );

  // D25 — show model badges only when the agent has used 2+ distinct models.
  const modelBadgeActive = useMemo(() => {
    return distinctModelsInHistory(messages).size >= 2;
  }, [messages]);

  // S6.5 — index permission_request / ask_user_question by request_id so we
  // render ONE card per request even though there are 2+ messages per
  // request_id (pending → terminal). Also identify the chronologically
  // earliest pending request id so that — at most — one card is sticky.
  const permissionsByRequestId = useMemo(() => {
    const map = new Map<
      string,
      {
        pending?: PermissionPendingContent;
        ask?: AskUserQuestionPendingContent;
        terminal?: PermissionTerminalContent;
        // The original `pending` message id, used as the React key + for
        // routing in the renderer below. We render at the position of the
        // pending message to keep stream order intact.
        pendingMessageId?: string;
      }
    >();
    for (const m of messages) {
      if (m.type !== 'permission_request' && m.type !== 'ask_user_question') continue;
      const rid = getRequestId(m.content);
      if (!rid) continue;
      const entry = map.get(rid) ?? {};
      if (isAskUserQuestionPending(m.content)) {
        entry.ask = m.content;
        entry.pendingMessageId = m.id;
      } else if (isPermissionPending(m.content)) {
        entry.pending = m.content;
        entry.pendingMessageId = m.id;
      } else if (isPermissionTerminal(m.content)) {
        entry.terminal = m.content;
      }
      map.set(rid, entry);
    }
    return map;
  }, [messages]);

  // First (chronologically earliest) pending request id with no terminal
  // yet — that's the one that gets the sticky pin. Multi-pending uses
  // stream-order; subsequent cards render inline without sticky.
  const stickyRequestId = useMemo(() => {
    for (const m of messages) {
      if (m.type !== 'permission_request' && m.type !== 'ask_user_question') continue;
      const rid = getRequestId(m.content);
      if (!rid) continue;
      const entry = permissionsByRequestId.get(rid);
      if (entry && !entry.terminal && (entry.pending || entry.ask)) return rid;
    }
    return null;
  }, [messages, permissionsByRequestId]);

  // Set of message ids to SKIP in the normal render pass. Terminal
  // permission rows are rendered (as PermissionOutcomePill) by the SAME
  // slot as the pending row, so we hide the duplicate terminal message.
  const hiddenMessageIds = useMemo(() => {
    const hidden = new Set<string>();
    for (const m of messages) {
      if (m.type !== 'permission_request' && m.type !== 'ask_user_question') continue;
      if (isPermissionTerminal(m.content)) hidden.add(m.id);
    }
    return hidden;
  }, [messages]);

  // Sprint 12 (D-008) — hide system lifecycle messages in founder mode.
  const developerMode = useSettingsStore((s) => s.developerMode);

  // Sprint 6 (Phase 2, D48) — aggregation. Filter out paired tool_results
  // BEFORE aggregating so they don't break runs of consecutive same-tool
  // calls (the aggregation pass treats any non-tool_use as a run-breaker).
  // S6.5 — also filter out the duplicate terminal permission rows; their
  // content is rendered by the pending message's slot (collapsed to
  // PermissionOutcomePill).
  // Sprint 12 — also filter out system lifecycle messages in founder mode.
  const renderItems = useMemo(() => {
    const filtered = topLevel.filter((m) => {
      if (!developerMode && m.type === 'system') {
        const content = m.content as { subtype?: string } | null;
        const subtype = content?.subtype;
        if (typeof subtype === 'string' && HIDDEN_SYSTEM_SUBTYPES.has(subtype)) {
          return false;
        }
      }
      if (m.type === 'tool_result' && pairedResultIds.has(m.id)) return false;
      if (hiddenMessageIds.has(m.id)) return false;
      // Sprint ai-elements-rendering (spec §3.4) — the D-msg-6 filter that
      // dropped `thinking_block` here is retired: MessageBubble now renders
      // a Reasoning-style duration line for it, so the wrapper is no longer
      // the empty div that produced D-msg-6's phantom `space-y` gaps.
      // Keeping thinking blocks in the stream also restores aggregate.ts's
      // documented run-breaker behaviour for them.
      return true;
    });
    return aggregateMessages({
      topLevel: filtered,
      resultByToolUseId,
    });
  }, [topLevel, pairedResultIds, resultByToolUseId, hiddenMessageIds, developerMode]);

  // Sprint 6 (Phase 3, D49) — synthesize per-turn summary cards keyed by the
  // `system_event` `result` message that closed each turn. Pure render-time
  // projection; never persisted.
  const summariesByResultId = useMemo(
    () => synthesizeTurnSummaries(messages),
    [messages],
  );

  // Sprint 6 (Phase 5, D39) — derive plan progress from the latest TodoWrite.
  const progress = useMemo(() => deriveProgress(messages), [messages]);

  // Sprint sticky-message-section-stacking-cursor-parity — group the
  // flat renderItems into per-turn buckets. A "turn" starts at any
  // user message and contains every render item up to (but not
  // including) the next user message. The first bucket (if it holds
  // any items before the first user message) is keyed `__lead__` and
  // does not contain a sticky child. Each bucket gets its own
  // `<section data-turn-id>` wrapper that becomes the CSS sticky
  // containing block for the user message inside it — that's what
  // produces the natural push-out behaviour as one section's bottom
  // edge meets the next section's top edge during scroll.
  const turns = useMemo(() => groupIntoTurns(renderItems), [renderItems]);

  // Sprint 6 (Phase 5, D44) — diff pills per TodoWrite emit (skip the first;
  // first emit has no prior to diff against). Keyed by the TodoWrite message
  // id so MessageBubble can pass them through to the TodoChecklist render.
  const todoPillsByMessageId = useMemo(() => {
    const out = new Map<string, import('../lib/tool-rendering/diff-todos').TodoUpdatePill[]>();
    const emits = collectTodoEmitsLocal(messages);
    for (let i = 1; i < emits.length; i += 1) {
      const prev = emits[i - 1];
      const cur = emits[i];
      if (!prev || !cur) continue;
      const pills = diffTodos(parseTodos(getTodoInput(prev)), parseTodos(getTodoInput(cur)));
      if (pills.length > 0) out.set(cur.id, pills);
    }
    return out;
  }, [messages]);

  const renderCount = renderItems.length + (partialActive ? 1 : 0);

  // Sprint 15 — tail marker lets useChatScroll skip the pill bump + auto-
  // scroll when growth comes from prepend (loadOlderMessages) instead of
  // appendMessage. Otherwise paginating up would spuriously fire
  // "↓ N new messages" and try to scroll the user to the bottom.
  const tailId = messages[messages.length - 1]?.id ?? null;

  const { containerRef, newCount, scrollToBottom } = useChatScroll({
    agentId,
    messageCount: renderCount,
    partialActive,
    tailId,
  });

  // Sprint 15 — lazy upward pagination + anchor scroll. Fires loadOlder
  // when the user scrolls within 200px of the top; restores the visual
  // scroll position after the prepend lands.
  const { hasMore, loadingOlder, loadOlder } = usePaginatedHistory(agentId);
  useInfiniteScrollUp({
    scrollContainerRef: containerRef,
    hasMore,
    loadingOlder,
    loadOlder,
    headId: messages[0]?.id ?? null,
  });

  // Sprint sticky-message-section-stacking-cursor-parity — the IO +
  // component-swap sticky machinery is gone. StickyProgressBar now
  // writes its measured height into the scroll container as the CSS
  // custom property `--user-sticky-top`, which UserMessage stickies
  // consume as their `top:` offset. No JS state, no observers,
  // no React re-render storm on resize.

  return (
    <div className="relative flex-1 min-h-0">
      <div
        ref={containerRef}
        className="absolute inset-0 overflow-y-auto"
        data-testid="message-list"
        data-agent-id={agentId}
        {...(office ? { 'data-surface': 'office' } : {})}
      >
        <StickyProgressBar progress={progress} />
        <div
          className={
            office
              ? 'w-full px-3 md:px-4 py-4 space-y-4'
              : 'max-w-reading mx-auto px-3 md:px-6 py-4 space-y-4'
          }
        >
          {/* Sprint 15 — lazy upward pagination spinner. Renders inline at
              the top so the anchor-scroll layout-effect treats it as part
              of the prepended block. Only visible while loadOlderMessages
              is in flight AND there are more messages to fetch. */}
          {hasMore && loadingOlder && (
            <div
              className="text-center text-body text-muted-foreground/70 py-2"
              data-testid="history-loading-older"
            >
              {t('chatView.loadingOlder')}
            </div>
          )}
          {topLevel.length === 0 && !partialActive && (
            <div className="text-center text-body text-muted-foreground/70 py-12">
              {t('chatView.noMessagesYet')}
            </div>
          )}

          {turns.map((turn) => (
            // Sprint sticky-message-section-stacking-cursor-parity —
            // every turn renders as its own `<section>`. The user
            // message inside is `position: sticky; top:
            // var(--user-sticky-top, 0px)`, so the browser pegs it to
            // the top of the section's containing block (this section)
            // as the user scrolls, and naturally pushes it out when
            // the section's bottom edge — which is the next user
            // message's section top — reaches the viewport top. The
            // `__lead__` section holds anything that rendered before
            // the first user message (system pills, etc.) and has no
            // sticky child.
            <section
              key={turn.turnId}
              data-turn-id={turn.turnId}
              className="space-y-4"
            >
              {turn.items.map((item) => {
                // S6.5 — permission cards intercept BEFORE aggregation/turn-
                // summary logic so they always own the slot they live in.
                if (item.kind === 'single') {
                  const m = item.message;
                  if (m.type === 'permission_request' || m.type === 'ask_user_question') {
                    const rid = getRequestId(m.content);
                    if (rid) {
                      const entry = permissionsByRequestId.get(rid);
                      if (entry?.ask) {
                        return (
                          <div key={m.id} className="flex flex-col" data-message-id={m.id}>
                            <AskUserQuestionCard
                              agentId={agentId}
                              pending={entry.ask}
                              terminal={entry.terminal}
                              sticky={!entry.terminal && rid === stickyRequestId}
                            />
                          </div>
                        );
                      }
                      if (entry?.pending) {
                        return (
                          <div key={m.id} className="flex flex-col" data-message-id={m.id}>
                            <PermissionCard
                              agentId={agentId}
                              pending={entry.pending}
                              terminal={entry.terminal}
                              sticky={!entry.terminal && rid === stickyRequestId}
                            />
                          </div>
                        );
                      }
                    }
                    // Defensive: malformed permission_request with no
                    // request_id. Render nothing rather than blow up.
                    return null;
                  }
                }
                if (item.kind === 'aggregation') {
                  return (
                    <div
                      key={item.key}
                      className="flex flex-col"
                      data-message-id={item.key}
                      {...officeAccent}
                    >
                      <AggregationCard
                        item={item}
                        resultByToolUseId={resultByToolUseId}
                      />
                    </div>
                  );
                }
                const m = item.message;
                // Sprint 6 (Phase 3, D49) — turn summary card appears after the
                // existing "Turn complete · 4.5s" system pill for any completed
                // turn that had at least one tool call.
                const summary = summariesByResultId.get(m.id);
                if (summary) {
                  return (
                    <div key={m.id} className="flex flex-col" data-message-id={m.id}>
                      <MessageBubble message={m} showModelBadge={modelBadgeActive} />
                      <TurnSummaryCard summary={summary} />
                    </div>
                  );
                }
                // Sprint ai-elements-rendering (spec §7) — orphan child:
                // parent_tool_use_id points at a pruned/truncated parent.
                // The message renders at root; the first of each orphan
                // group gets a dev-only notice.
                const orphanNotice = orphanNoticeIds.has(m.id) ? (
                  <OrphanSubagentNotice />
                ) : null;
                // Subagent (Agent / Task) → render the recursive tree
                // (sprint ai-elements-rendering: the panel receives the
                // full childrenByParent map and recurses on nested
                // Task/Agent calls itself).
                if (m.type === 'tool_use') {
                  const tu = asToolUse(m);
                  if (SUBAGENT_TOOL_NAMES.has(tu.name) && tu.tool_use_id) {
                    const result = resultByToolUseId.get(tu.tool_use_id);
                    return (
                      <div
                        key={m.id}
                        className="flex flex-col"
                        data-message-id={m.id}
                      >
                        {orphanNotice}
                        <SubagentPanel
                          toolUseMessage={m}
                          childrenByParent={childrenByParent}
                          resultMessage={result}
                        />
                      </div>
                    );
                  }
                  const pairedResult = tu.tool_use_id
                    ? resultByToolUseId.get(tu.tool_use_id)
                    : undefined;
                  return (
                    <div
                      key={m.id}
                      className="flex flex-col"
                      data-message-id={m.id}
                      {...officeAccent}
                    >
                      {orphanNotice}
                      <MessageBubble
                        message={m}
                        toolResultMessage={pairedResult}
                        showModelBadge={modelBadgeActive}
                        todoUpdatePills={todoPillsByMessageId.get(m.id)}
                      />
                    </div>
                  );
                }
                // User messages render bare (no flex-col wrapper) so
                // their own `position: sticky` is bounded by the
                // surrounding `<section>` rather than a wrapper div.
                if (m.type === 'user') {
                  return (
                    <MessageBubble
                      key={m.id}
                      message={m}
                      showModelBadge={modelBadgeActive}
                      onEditMessage={onEditMessage}
                      onResendMessage={onResendMessage}
                      agentId={agentId}
                    />
                  );
                }
                return (
                  <div
                    key={m.id}
                    className="flex flex-col"
                    data-message-id={m.id}
                    // E-M5: system pills never carry the office accent.
                    {...(m.type !== 'system' ? officeAccent : {})}
                  >
                    {orphanNotice}
                    <MessageBubble
                      message={m}
                      showModelBadge={modelBadgeActive}
                    />
                  </div>
                );
              })}
            </section>
          ))}

          {partialActive && <PartialIndicator text={partialText ?? ''} />}
          {!partialActive && liveActivity && <LiveActivity activity={liveActivity} />}
          {/* Sprint 20.3 PR-7 (D013-EC-22) — live-stream truncated pill.
              Renders only when the per-agent backpressure cap was hit
              this turn. The DB has the persisted message; clicking
              reloads from the server. */}
          <StreamTruncatedPill agentId={agentId} />
        </div>
      </div>

      <ScrollToBottomPill count={newCount} onClick={() => scrollToBottom(true)} />
    </div>
  );
}

// Sprint ai-elements-rendering (spec §7) — dev-only marker above the
// first message of an orphan subagent group. The founder never sees it
// (protocol breakage is developer-shaped); a developer debugging a
// truncated history gets the signal.
function OrphanSubagentNotice() {
  const { t } = useTranslation();
  return (
    <DeveloperOnly>
      <div
        className="text-caption text-muted-foreground/70 mb-1"
        data-testid="orphan-subagent-notice"
      >
        {t('subagent.orphanNotice')}
      </div>
    </DeveloperOnly>
  );
}

interface Turn {
  /**
   * Stable key for the section. `__lead__` for pre-conversation content
   * (system pills before any user input); the user message id
   * otherwise.
   */
  turnId: string;
  items: RenderItem[];
}

/**
 * Split the flat render-item list into per-turn sections. A turn boundary
 * is any item whose underlying message has `type === 'user'`. The first
 * bucket — anything before the first user message — gets the synthetic
 * id `__lead__` and renders without a sticky child.
 *
 * Sprint sticky-message-section-stacking-cursor-parity.
 */
function groupIntoTurns(items: RenderItem[]): Turn[] {
  const turns: Turn[] = [];
  let current: Turn = { turnId: '__lead__', items: [] };
  for (const it of items) {
    const isUser = it.kind === 'single' && it.message.type === 'user';
    if (isUser) {
      // Close the lead bucket only if it actually accumulated content;
      // an empty `__lead__` would add a stray empty section.
      if (current.items.length > 0 || current.turnId !== '__lead__') {
        turns.push(current);
      }
      current = { turnId: it.message.id, items: [it] };
    } else {
      current.items.push(it);
    }
  }
  if (current.items.length > 0) turns.push(current);
  return turns;
}
